import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from "react";
import { ConversationProvider } from "@elevenlabs/react";
import App from "./App";
import { AuthProvider, AuthScreen } from "./auth/index";
import { I18nProvider } from "./i18n";
import { MatchApp } from "./matching/MatchApp";
import { ReviewApp } from "./review-console/ReviewApp";

/** Voice must never white-screen the app for testers. */
class OptionalVoice extends Component<{ children: ReactNode }, { dead: boolean }> {
  state = { dead: false };

  static getDerivedStateFromError() {
    return { dead: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[UN8] voice provider failed — continuing without it", error, info.componentStack);
  }

  render() {
    if (this.state.dead) return this.props.children;
    return <ConversationProvider>{this.props.children}</ConversationProvider>;
  }
}

function routeFromHash(hash: string): "onboarding" | "review" | "match" | "auth" {
  const path = hash.replace(/^#\/?/, "");
  if (path === "auth" || path.startsWith("auth/")) {
    return "auth";
  }
  if (
    path === "review" ||
    path.startsWith("review/") ||
    path === "admin" ||
    path.startsWith("admin/")
  ) {
    return "review";
  }
  if (path === "match" || path.startsWith("match/")) {
    return "match";
  }
  return "onboarding";
}

function Router() {
  const [route, setRoute] = useState(() => routeFromHash(window.location.hash));

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  if (route === "auth") return <AuthScreen />;
  if (route === "review") return <ReviewApp />;
  if (route === "match") return <MatchApp />;
  return <App />;
}

export default function Root() {
  return (
    <I18nProvider>
      <AuthProvider>
        <OptionalVoice>
          <Router />
        </OptionalVoice>
      </AuthProvider>
    </I18nProvider>
  );
}
